import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'emerge.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function apply(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  // Keeps native controls, scrollbars and form widgets in the same theme as
  // the tokens; without it they stay light on a dark page.
  document.documentElement.style.colorScheme = resolved;
}

/**
 * Theme preference: follow the OS, or override it.
 *
 * The initial attribute is set by an inline script in `index.html` so there is
 * no flash before React mounts; this hook keeps it in sync afterwards. Reading
 * during initial state (rather than in an effect) means the switcher renders
 * the correct selection on the first paint too.
 */
export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(readMode);
  const [systemIsDark, setSystemIsDark] = useState(() => systemTheme() === 'dark');

  // Following the OS means reacting when it changes, not just at load.
  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemIsDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = mode === 'system' ? (systemIsDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    apply(resolved);
  }, [resolved]);

  const selectMode = useCallback((next: ThemeMode) => {
    setMode(next);
    try {
      // 'system' is stored as an absence, so a machine that later changes its
      // OS setting is followed rather than pinned to whatever it was that day.
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting is a convenience; the session still works without it.
    }
  }, []);

  return { mode, resolved, selectMode };
}
